import {
  computed,
  ComputedRef,
  Ref,
  unref,
  ref,
  MaybeRef,
  markRaw,
  onActivated,
  onBeforeUnmount,
  onDeactivated,
  watch,
} from 'vue'
import { ApolloClient } from '@apollo/client/core'
import { TypedDocumentNode } from '@graphql-typed-document-node/core'
import {
  ExecutableDefinitionNode,
  Kind,
  print,
  SelectionNode,
  TypedQueryDocumentNode,
  TypeKind,
} from 'graphql/index'
import gql from 'graphql-tag'
import Fuse from 'fuse.js'
import { useLocalStorage, RemovableRef } from '@vueuse/core'
import GlobalEncoder from '@/utils/Encoders/GlobalEncoder'
import {
  OrganisationFragmentDef,
  CompanyAccountsQuery,
  CompanyFragmentDef,
  CompanyOrganisationsFragmentDef,
  FieldTypeFragmentDef,
  FullTypeFragmentDef,
  InputValueFragmentDef,
  IntrospectionQuery,
  TypeRefFragmentDef,
} from '@/components/GraphqlDataTable/queries'
import { useFragment } from '@/gql'
import {
  FullTypeFragment,
  CompanyAccountFragment,
  InputValueFragment,
  FieldTypeFragment,
  OrganisationAccountFragment,
} from '@/gql/graphql'
import { ListItem } from '@/components/Workflows/Pages/Workflow/workflow'
import { provider } from '@/app-bootstrapping/SetUpApollo'
import LookupFilters from '@/components/GraphqlDataTable/filters'
import { FilterTypes } from '@/components/GraphqlDataTable/filters/filter-enums'
import {
  convertArgTypeToTypeStr,
  decodeExpandedColumnKey,
  fieldToReadAble,
  generateSortOptions,
  getFieldTypeLeaf,
  tableNamespace,
} from '@/components/GraphqlDataTable/tools'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { FilterMetaDataProp } from '@/components/GraphqlDataTable/filters/types'
import ColumnMap from '@/components/GraphqlDataTable/columns'
import BaseDataTable from '@/components/BaseDataTable/BaseDataTable.vue'
import {
  ColumnFragmentListSupport,
  type GraphqlColumnMap,
  type ListSupportExpand,
} from '@/components/GraphqlDataTable/columns/types'
import { deepClone, dotAccess } from '@/utils/tools'
import DateColumn from '@/components/GraphqlDataTable/columns/DateColumn.vue'
import { TdText } from '@/components/BaseDataTable/Cells'
import { Column } from '@/vue-prop-types/DataTable/types'

const WarningKey = '[GRAPHQL-DATATABLE-WARN]'
const OperationDefinition = 'OperationDefinition'

function customProvide<TResult = any>(
  client: ApolloClient<any>,
  query: TypedDocumentNode<TResult, any>,
) {
  const loading = ref(true)
  const result: Ref<TResult | undefined> = ref()
  client
    .query<TResult>({ query })
    .then((data) => {
      result.value = data.data
    })
    .finally(() => {
      loading.value = false
    })

  return {
    loading,
    result,
  }
}

/**
 * @param {string} clientName The name of the apollo client we want to grab introspection information on
 */
export const useIntrospectionTypes = (
  clientName: string = 'publicApiCustomFields',
): {
  introspection: any
  typeLookup: ComputedRef<Record<string, FullTypeFragment>>
  queryType: ComputedRef<FullTypeFragment | null>
  loading: Ref<boolean>
} => {
  const client = provider.clients[clientName]
  if (!client) throw new Error(`Client not found, was given: ${clientName}`)

  const { loading, result: introspection } = customProvide(client, IntrospectionQuery)

  const typeLookup: ComputedRef<Record<string, FullTypeFragment>> = computed(() => {
    if (loading.value || !introspection.value?.__schema) return {} // Return empty object if data is still loading

    // Destructure types from the introspection result
    const {
      __schema: { types },
    } = introspection.value

    // Reduce the types array into an object with type names as keys
    return (
      types
        .map((type) => useFragment(FullTypeFragmentDef, type))
        .filter(({ name }: FullTypeFragment) => name !== null && name !== undefined)
        // Since we just filtered out nulls and undefineds its safe to do a forced cast here
        .reduce((acc, type) => ({ ...acc, [type.name as string]: type }), {})
    )
  })

  // Contains information on the Query type of the client. Basically contains information on all our endpoints
  const queryType: ComputedRef<FullTypeFragment | null> = computed(() => {
    if (loading.value || !introspection) return null // Return null if we don't have data yet

    // Destructure queries from the introspection result
    return useFragment(FullTypeFragmentDef, introspection.value?.__schema.queryType) ?? null
  })

  return { typeLookup, queryType, introspection, loading }
}

/**
 * Fetches the authenticated users accounts and organisations accounts and returns them all in an array
 * @param clientName The name of the apollo client we want to grab introspection information on
 * @param useSubdivisions If we should include the organisations subdivisions for companies parent organisations
 */
export const useCompanyAccounts = ({
  clientName = 'publicApiCustomFields',
  useSubdivisions = false,
} = {}): {
  accounts: ComputedRef<(ListItem & (CompanyAccountFragment | OrganisationAccountFragment))[]>
  loading: Ref<boolean>
} => {
  const client = provider.clients[clientName]
  if (!client) throw new Error(`Client not found, was given: ${clientName}`)
  const { result, loading } = customProvide(client, CompanyAccountsQuery)

  // Computed property to process and flatten the accounts data
  const accounts: ComputedRef<
    (ListItem & (CompanyAccountFragment | OrganisationAccountFragment))[]
  > = computed(() => {
    const seen = {} // Object to keep track of processed account IDs to avoid duplicates

    const flatOrganisations = (org: OrganisationAccountFragment) => [
      org,
      ...org.divisions.data.map((c) => useFragment(CompanyFragmentDef, c)),
    ]

    return (
      result.value?.accounts
        .flatMap((acc) => {
          // If the account is an 'Organisation', include its divisions
          if (acc.__typename === 'Organisation') {
            const org = useFragment(OrganisationFragmentDef, acc)
            // Return an array containing the organisation and its divisions
            return flatOrganisations(org)
          }
          // Otherwise, return the account as is
          if (acc.__typename === 'Company') {
            return [
              useFragment(CompanyFragmentDef, acc),
              ...(useSubdivisions
                ? useFragment(CompanyOrganisationsFragmentDef, acc)
                    .organisations.data.map((org) => useFragment(OrganisationFragmentDef, org))
                    .flatMap(flatOrganisations)
                : []),
            ]
          }
          return []
        })
        .filter((acc) => {
          // Filter out duplicate accounts based on their ID
          if (acc !== undefined && seen.hasOwnProperty(acc.id)) return false
          seen[acc.id] = true
          return true
        })
        .map((acc: OrganisationAccountFragment | CompanyAccountFragment) => ({
          // Map each account to an object with 'value', 'label', and other account properties
          value: acc.id,
          label: acc.name,
          ...acc,
        })) ?? []
    )
  })

  return { accounts, loading }
}

const ARG_BREAK_TOKEN = '<ARG_BREAK>'
// @ts-expect-error ts hates the named capturing group
const queryArgsReg = /query[^(]+[(](?<args>[^)]+)/
// @ts-expect-error ts hates the named capturing groups
const queryTypeReg = /query.+\{[ \n]+(?<query>[^(]+)\((?<args>[^)]+)/
// @ts-expect-error ts hates the named capturing groups
const queryNameReg = /query\s+(?<name>.+)\(/
// Function takes and cleans + normalizes the argument strings, like $foo: Bar, $bar: Foo\n $Cee: $Doo\n
// Will instead become $foo: Bar<ARG_BREAK> $bar: Foo<ARG_BREAK> $Cee: $Doo
// Making it easier to split the arguments cleanly
const argumentCleaning = (argStrs: string | undefined) =>
  argStrs?.trim()?.replaceAll(',', ARG_BREAK_TOKEN)?.replaceAll('\n', ARG_BREAK_TOKEN)

// Returns the main type the query is receiving. Ie are we querying hires, then it returns the Hire type information
// It also returns the arguments that are part of the query. Including the inputted args and all args that are part of
// the query itself.
export const useQueryType = (
  query: string | Ref<string>,
  clientName: string = 'publicApiCustomFields',
): {
  args: ComputedRef<{
    inputtedArgs: InputValueFragment[]
    allArgs: InputValueFragment[]
    argNameToInputtedArgName: Record<string, string>
  }>
  type: ComputedRef<null | FieldTypeFragment>
  regexResults: ComputedRef<{
    args: { name: string; typeStr: string }[]
    queryTypeStr: string
    queryArgsDefStr: string
    queryArgsMap: Record<string, string>
  }>
  queryName: ComputedRef<string>
} => {
  const queryName = computed(
    () => unref(query).match(queryNameReg)?.groups?.name ?? 'undefined-query',
  )

  const qRegs = computed(() => {
    const q = unref(query)
    const argsStr = q.match(queryArgsReg)?.groups?.args
    const { query: queryTypeStr, args: queryArgsDefStr } = q.match(queryTypeReg)?.groups ?? {}

    const args =
      argumentCleaning(argsStr)
        ?.split(ARG_BREAK_TOKEN)
        ?.map((arg) => arg.trim().split(':'))
        ?.map(([name, typeStr]) => ({ name, typeStr })) ?? []

    const queryArgsMap: Record<string, string> =
      argumentCleaning(queryArgsDefStr)
        ?.split(ARG_BREAK_TOKEN)
        .map((arg) => arg.trim().split(':'))
        .reduce((acc, [argName, inputArg]) => ({ ...acc, [inputArg.trim()]: argName }), {}) ?? {}

    return { args, queryTypeStr, queryArgsDefStr, queryArgsMap }
  })

  const { queryType } = useIntrospectionTypes(clientName)

  const type: ComputedRef<null | FieldTypeFragment> = computed(() => {
    const typeSearched = queryType.value?.fields
      ?.map((f) => useFragment(FieldTypeFragmentDef, f))
      .find(({ name }) => qRegs.value.queryTypeStr === name)
    if (!typeSearched) return null
    return typeSearched
  })

  const args: ComputedRef<{
    inputtedArgs: InputValueFragment[]
    allArgs: InputValueFragment[]
    argNameToInputtedArgName: Record<string, string>
  }> = computed(() => {
    const { args: argNames, queryArgsMap } = qRegs.value

    const allArgs = type.value?.args.map((a) => useFragment(InputValueFragmentDef, a)) ?? []
    // These are the arguments that have been inputted in the query.
    const inputtedArgs: InputValueFragment[] = argNames
      .map(({ name: argName }) => allArgs.find(({ name }) => queryArgsMap?.[argName] === name))
      .filter((v): v is InputValueFragment => v !== undefined)

    // Maps the argument names used in the query to the argument names that are inputted
    // query MyQuery($myInput: string) {
    //     myQuery(myOtherInput: $myInput) {
    //        ....
    //     }
    // }
    // Would result in a mapping like { myOtherInput: myInput }
    const argNameToInputtedArgName: Record<string, string> = Object.keys(queryArgsMap).reduce(
      (acc, name) => ({ ...acc, [queryArgsMap[name]]: name.replace('$', '') }),
      {},
    )
    return { inputtedArgs, allArgs, argNameToInputtedArgName }
  })

  return { type, args, regexResults: qRegs, queryName }
}

export const useQueryArgs = (
  query: MaybeRef<string>,
  config: MaybeRef<{
    generateAllArgs?: MaybeRef<boolean> // Generate all arguments
    filterBlackList?: MaybeRef<string[]> // Avoid generating arguments from this list
    onlyArgs?: MaybeRef<string[]> // Only generate arguments from this list
  }> = {
    generateAllArgs: false,
    filterBlackList: [],
    onlyArgs: [],
  },
) => {
  const { type: queryType, args: queryArgs } = useQueryType(query)
  const { typeLookup, loading } = useIntrospectionTypes()
  const { generateAllArgs, filterBlackList, onlyArgs } = unref(config)

  const onlyArgsArray = computed(() => unref(onlyArgs) ?? [])
  const onlyArgsEnabled = computed(() => onlyArgsArray.value.length > 0)

  const enumTypeToSelector = (
    { leaf, arg, listType: multiSelect },
    inputArgName: string,
    filterKeys: string[],
  ) => {
    const { enumValues } = typeLookup.value[leaf.name]
    return {
      list:
        enumValues?.map((value) => ({
          ...value,
          label: $t(value.description),
          value: value.name,
        })) ?? [],
      title: $t(queryArgs.value.argNameToInputtedArgName[arg.name]),
      inputArgName,
      name: arg.name,
      filterKeys,
      description: arg.description,
      placeholder: $t('All'),
      multiSelect,
      type: FilterTypes.SELECT,
    }
  }

  const scalarTypeToSelector = (
    { leaf, arg, listType },
    inputArgName: string,
    filterKeys: string[],
  ) => {
    switch (leaf.name) {
      case 'Boolean':
        return {
          type: FilterTypes.TOGGLE,
          inputArgName,
          filterKeys,
          name: arg.name,
          title: arg.name,
          description: arg.description,
        }
      case 'String':
        if (listType && import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.warn(
            WarningKey,
            `We don't support generating multi select for dynamic strings as the conditions are too vague.\nBuild a dedicated filter to support the graphql input ${inputArgName} with one of the following keys`,
            filterKeys,
          )
          return null
        }
        // Do stuff here
        return {
          type: FilterTypes.SINGLE_STRING,
          inputArgName,
          filterKeys,
          name: arg.name,
          title: arg.name,
          description: arg.description,
        }
      case 'ID':
        if (listType && import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.warn(
            WarningKey,
            `We don't support generating multi select for dynamic IDs as the conditions are too vague.\nBuild a dedicated filter to support the graphql input ${inputArgName} with one of the following keys`,
            filterKeys,
          )
          return null
        }
        return {
          type: FilterTypes.SINGLE_STRING,
          inputArgName,
          filterKeys,
          name: arg.name,
          title: arg.name,
          description: arg.description,
        }
      default:
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.warn(
            WarningKey,
            `We don't support generating a scalar type filter for the type ${arg.name}.\nBuild a dedicated filter to support the graphql input for this type with one of the following keys`,
            filterKeys,
          )
        }
        return null
    }
  }

  const containsListType = (field) => {
    let { type } = field
    // Unwrap the type until there is no more 'ofType'
    do {
      const { ofType, kind } = type
      if (kind === 'LIST') return true
      type = ofType
    } while (type)

    return false
  }

  const blackList = computed(() => ['first', 'page', 'orderBy', ...(unref(filterBlackList) ?? [])])

  const argToSpecificKey = (arg) => `${arg.name}-${convertArgTypeToTypeStr(arg.type)}`
  const argToGenericKey = (arg) => `${convertArgTypeToTypeStr(arg.type)}`

  const dynamicArgs: ComputedRef<FilterMetaDataProp[]> = computed(() => {
    const selected = queryType.value
    if (!selected) return []

    const hardCodedArgs = blackList.value
    const inputtedArgNames = queryArgs.value.inputtedArgs.map(({ name }) => name)
    const argMapping = queryArgs.value.argNameToInputtedArgName
    const ENUM_TYPE = 'ENUM'
    const SCALAR_TYPE = 'SCALAR'
    // Build the arguments string for the query
    const args = selected.args
      .map((arg) => useFragment(InputValueFragmentDef, arg))
      .filter((arg) => {
        const argName = arg.name
        if (onlyArgsEnabled.value) {
          return onlyArgsArray.value.includes(argMapping[argName])
        }
        return (
          !hardCodedArgs.includes(argName) &&
          (unref(generateAllArgs) || inputtedArgNames.includes(argName))
        )
      })
      .map((arg) => ({
        arg,
        leaf: getFieldTypeLeaf(useFragment(TypeRefFragmentDef, arg.type)),
        listType: containsListType(arg),
      }))

    // We sort the args to align as best we can with the inputtedArgs to allow control over the ordering
    args.sort(
      ({ arg: argA }, { arg: argB }) =>
        inputtedArgNames.findIndex((name) => argA.name === name) -
        inputtedArgNames.findIndex((name) => argB.name === name),
    )

    return (
      args
        .map((arg) => {
          // This mapping function will try to find a component that will map an argument to a function in the following order
          // Given the query
          // query SomeQuery($arg1: Type) {
          //    someQuery(arg2: $arg1) {
          //
          //    }
          // }
          // First it will check for an arg with the key arg1-Type
          // then it will check for arg2-Type
          // and lastly for Type
          const argKeyCustomSpecific = `${argMapping[arg.arg.name]}-${convertArgTypeToTypeStr(arg.arg.type)}`
          const argKeySpecific = argToSpecificKey(arg.arg)
          const argKeyGeneric = argToGenericKey(arg.arg)
          const filterKeys = [argKeyCustomSpecific, argKeySpecific, argKeyGeneric]
          if (
            LookupFilters[argKeyCustomSpecific] &&
            LookupFilters[argKeyCustomSpecific].component
          ) {
            return {
              arg,
              dynamicArg: {
                type: FilterTypes.COMPONENT,
                component: LookupFilters[argKeyCustomSpecific].component,
                inputArgName: argMapping[arg.arg.name],
                filterKeys,
                name: argMapping[arg.arg.name],
                description: arg.arg.description,
              },
            }
          }
          if (LookupFilters[argKeySpecific] && LookupFilters[argKeySpecific].component) {
            return {
              arg,
              dynamicArg: {
                type: FilterTypes.COMPONENT,
                component: LookupFilters[argKeySpecific].component,
                filterKeys,
                inputArgName: argMapping[arg.arg.name],
                name: arg.arg.name,
                description: arg.arg.description,
              },
            }
          }
          if (LookupFilters[argKeyGeneric] && LookupFilters[argKeyGeneric].component) {
            return {
              arg,
              dynamicArg: {
                type: FilterTypes.COMPONENT,
                component: LookupFilters[argKeyGeneric].component,
                filterKeys,
                inputArgName: argMapping[arg.arg.name],
                name: arg.arg.name,
                description: arg.arg.description,
              },
            }
          }

          switch (arg.leaf.kind) {
            case SCALAR_TYPE:
              return {
                arg,
                dynamicArg: scalarTypeToSelector(arg, argMapping[arg.arg.name], filterKeys),
              }
            case ENUM_TYPE:
              return {
                arg,
                dynamicArg: enumTypeToSelector(arg, argMapping[arg.arg.name], filterKeys),
              }
          }
          if (import.meta.env.DEV) {
            // eslint-disable-next-line no-console
            console.warn(
              WarningKey,
              `Failed to solve filter for ${arg.arg.name}. Implement a custom filer with the one of the keys`,
              filterKeys,
              `to enable this filter`,
            )
          }
          return { arg, dynamicArg: null }
        })
        .filter(({ dynamicArg }) => !!dynamicArg)
        .map(({ arg, dynamicArg }) => ({
          filter: dynamicArg as NonNullable<typeof dynamicArg>,
          graphqlData: arg,
        }))
        // Here we enforce the type since we have filtered out the nullable options in our filter
        .map((filter) => filter as unknown as FilterMetaDataProp)
    )
  })

  return { args: dynamicArgs, loading }
}

export const useQueryArg = (query: MaybeRef<string>, arg: MaybeRef<string>) => {
  const { args, loading } = useQueryArgs(query, { onlyArgs: [unref(arg)] })
  const argName = computed(() => unref(arg))
  const dynamicArg = computed(() =>
    args.value.find((argument) => argument.filter.inputArgName === argName.value),
  )

  return { arg: dynamicArg, loading }
}

const INPUT_OBJECT = 'INPUT_OBJECT'

export const useOrderingType = (
  query: string | Ref<string>,
  clientName: string = 'publicApiCustomFields',
): {
  orderType: ComputedRef<FullTypeFragment | null>
  orderInputType: ComputedRef<FullTypeFragment | null>
  orderInputTypeFields: ComputedRef<InputValueFragment[] | null>
} => {
  const { args } = useQueryType(query, clientName)
  const { typeLookup } = useIntrospectionTypes()

  const orderInputType = computed(() => {
    const orderByArgs = args.value.inputtedArgs
      .filter((arg) => !!arg)
      .find(({ name }) => name === 'orderBy')

    if (!orderByArgs || !orderByArgs.type) return null

    const leaf = getFieldTypeLeaf(useFragment(TypeRefFragmentDef, orderByArgs.type))
    if (!leaf) return null
    const type = typeLookup.value[leaf.name ?? '']

    // Should be an input type
    const inputFields = useFragment(InputValueFragmentDef, type.inputFields)
    if (inputFields === undefined || inputFields === null) return null
    if (type.kind !== INPUT_OBJECT && inputFields.length > 0) return null

    return type
  })

  const orderInputTypeFields = computed(() => {
    if (orderInputType.value === null) return null
    const inputFields = useFragment(InputValueFragmentDef, orderInputType.value.inputFields)
    if (inputFields === undefined || inputFields === null) return null
    return inputFields
  })

  const orderType = computed(() => {
    if (orderInputTypeFields.value === null) return null
    const field = orderInputTypeFields.value.find((f) => f.name === 'field' || f.name === 'column')
    if (!field) return null
    const fieldLeaf = getFieldTypeLeaf(useFragment(TypeRefFragmentDef, field.type))
    if (!fieldLeaf) return null

    // OT stands for orderType. Just use OT here to avoid name collision
    const OT = typeLookup.value[fieldLeaf.name ?? '']
    if (!OT) return null

    return OT
  })

  return { orderType, orderInputType, orderInputTypeFields }
}

export const useRootType = (q: MaybeRef<string>) => {
  const { type: queryType } = useQueryType(q)
  const { typeLookup } = useIntrospectionTypes()
  const dataType = computed<null | FullTypeFragment>(() => {
    if (!queryType.value) return null
    const type = useFragment(TypeRefFragmentDef, queryType.value.type)
    const queryTypeLeaf = getFieldTypeLeaf(type)
    if (!queryTypeLeaf || !(queryTypeLeaf.name !== null && queryTypeLeaf.name !== undefined))
      return null
    const queryName = queryTypeLeaf.name
    const queryField = typeLookup.value[queryName]
    if (!queryField || !queryField.fields) return null
    const dataField = queryField.fields.find(
      (field) => useFragment(FieldTypeFragmentDef, field).name === 'data',
    )
    if (!dataField) return null
    // Return the name of the innermost type of the 'data' field
    const dataName = getFieldTypeLeaf(
      useFragment(TypeRefFragmentDef, useFragment(FieldTypeFragmentDef, dataField).type),
    ).name
    if (!dataName) return null
    return typeLookup.value[dataName] ?? null
  })

  return { dataType }
}

export const useModifiedQuery = (
  q: MaybeRef<string>,
  whiteList: MaybeRef<Record<string, boolean>>,
) => {
  const { regexResults } = useQueryType(q)

  const query = computed(() => gql(unref(q))) as ComputedRef<TypedQueryDocumentNode>

  const { dataType } = useRootType(q)

  const customizedQueries = computed(() => {
    // Figure out what columns the users has selected
    const defaultRes = { q: query.value, qp: query.value, pq: query.value }
    if (!dataType.value || !dataType.value.fields) return defaultRes

    const rootType = dataType.value.name

    const columns = Object.keys(unref(whiteList)).reduce((acc, key) => {
      if (ColumnMap[key]) {
        // LIST-mode columns piggyback on the base fragment already in the query.
        if (ColumnMap[key].listSupport?.supportType === ColumnFragmentListSupport.LIST) {
          return acc
        }
        return { ...acc, [key]: ColumnMap[key] }
      }
      const expanded = decodeExpandedColumnKey(key)
      if (expanded) {
        const baseColumn = ColumnMap[expanded.fragmentName]
        if (baseColumn?.listSupport?.supportType === ColumnFragmentListSupport.LIST) {
          return acc
        }
      }

      const fragDef = query.value.definitions.find((def) => def.name?.value === key)
      if (fragDef?.kind === Kind.FRAGMENT_DEFINITION) {
        return { ...acc, [key]: { frag: fragDef } }
      }
      const isFieldOnDataType = dataType.value?.fields?.some(
        (f) => useFragment(FieldTypeFragmentDef, f).name === key,
      )
      if (isFieldOnDataType) {
        return { ...acc, [key]: { field: key } }
      }
      return acc
    }, {})

    const definition = query.value.definitions.find(({ kind }) => kind === OperationDefinition)
    if (!definition || !definition.name) {
      return defaultRes
    }
    const queryName = definition.name.value
    // Modify the query by adding / removing fragments

    const queryArgString =
      regexResults.value.queryArgsDefStr.trim().length > 0
        ? `(${regexResults.value.queryArgsDefStr.replace(/orderBy:.+/g, '')})`
        : ''

    // Arguments the root query actually uses; match tokens, since print() only wraps past 80 chars.
    const usedArguments = [...queryArgString.matchAll(/\$\w+/g)].map(([v]) => v)

    const queryInputString = regexResults.value.args
      .reduce((acc, arg) => `${acc} ${arg.name}: ${arg.typeStr}\n`, '')
      .trim()

    // Ordering is removed for 2 reasons:
    // 1. Ordering has no effect on the paginated information
    // 2. Removing ordering differentiates our queries in the normalized cache
    //    preventing accidental overwrites of query cache information.
    const queryInputStringPagination = regexResults.value.args
      .filter((arg) => arg.name !== '$orderBy')
      .filter((arg) => usedArguments.includes(arg.name))
      .reduce((acc, arg) => `${acc} ${arg.name}: ${arg.typeStr}\n`, '')
      .trim()

    // Figure out how the mapping will work...
    const queryFragments = Object.keys(columns)
      .filter((fragName) => {
        if (!columns[fragName]) {
          if (import.meta.env.DEV) {
            // eslint-disable-next-line no-console
            console.warn('Unknown fragment name', fragName)
          }
          return false
        }
        return true
      })
      .reduce((acc, fragName) => {
        // Figure out the mapping need
        if (columns[fragName].field) {
          return `${acc}\n${columns[fragName].field}`
        }

        if (columns[fragName].frag) {
          return `${acc}\n...${fragName}`
        }

        const fragmentDataType = columns[fragName].fragment.definitions.find(
          ({ kind }) => kind === Kind.FRAGMENT_DEFINITION,
        ).typeCondition.name.value

        if (fragmentDataType === rootType) {
          return `${acc}\n...${fragName}`
        }

        const fieldPath =
          dataType.value?.fields
            ?.map((field) => useFragment(FieldTypeFragmentDef, field))
            .find(
              (field) =>
                getFieldTypeLeaf(useFragment(TypeRefFragmentDef, field.type)).name ===
                fragmentDataType,
            )?.name ?? ''

        return `${acc}\n${fieldPath} {
      ...${fragName}
    }`
      }, '')

    const rawFragments = Object.values(columns).reduce((acc, col: any) => {
      if (col.field) return acc
      if (col.frag) return `${acc}\n${print(col.frag)}`
      return `${acc}\n${print(col.fragment)}`
    }, '')

    if (queryFragments.length === 0) return defaultRes
    const queryString = `
    query ${queryName}${queryInputString.length > 0 ? `(${queryInputString})` : ''} {
      ${regexResults.value.queryTypeStr} ${regexResults.value.queryArgsDefStr.trim().length > 0 ? `(${regexResults.value.queryArgsDefStr})` : ''} {
        data {
          ${queryFragments}
        }
      }
    }

    ${rawFragments}
    `

    const pq = `
    query GraphqlDataTablePaginationQuery${queryInputStringPagination.length > 0 ? `(${queryInputStringPagination})` : ''} {
    ${regexResults.value.queryTypeStr} ${regexResults.value.queryArgsDefStr.trim().length > 0 ? `(${regexResults.value.queryArgsDefStr.replace(/orderBy:.+/g, '')})` : ''} {
      paginatorInfo {
        ...PaginatorInfoFragment
      }
    }
  }
  ${print((BaseDataTable as any).fragments.pagination)}`

    const qp = `
    query ${queryName}${queryInputString.length > 0 ? `(${queryInputString})` : ''} {
      ${regexResults.value.queryTypeStr} ${regexResults.value.queryArgsDefStr.trim().length > 0 ? `(${regexResults.value.queryArgsDefStr})` : ''} {
        data {
          ${queryFragments}
        }
        paginatorInfo {
          ...PaginatorInfoFragment
        }
      }
    }
    ${rawFragments}
    ${print((BaseDataTable as any).fragments.pagination)}
    `
    return {
      q: gql(queryString) as TypedQueryDocumentNode,
      qp: gql(qp) as TypedQueryDocumentNode,
      pq: gql(pq) as TypedQueryDocumentNode,
    }
  })

  const modifiedQuery = computed(() => customizedQueries.value.q)
  const fullModifiedQuery = computed(() => customizedQueries.value.qp)
  const paginatedQuery = computed(() => customizedQueries.value.pq)
  // False while the schema type is unresolved and pq is still the page's own query.
  const paginationQueryReady = computed(() => customizedQueries.value.pq !== query.value)

  return { modifiedQuery, fullModifiedQuery, paginatedQuery, paginationQueryReady }
}

export const useQueryColumns = (
  q: MaybeRef<string | TypedQueryDocumentNode>,
): {
  columns: ComputedRef<{ name: string; columnDescriber: GraphqlColumnMap }[]>
  fieldToColumn: (field: FieldTypeFragment | undefined) => GraphqlColumnMap | null
} => {
  const query = computed(() => {
    const unwrapped = unref(q)
    if (typeof unwrapped === 'string') return gql(unwrapped)
    return unwrapped
  }) as ComputedRef<TypedQueryDocumentNode>

  const qstr = computed(() => {
    const unwrapped = unref(q)
    if (typeof unwrapped === 'string') return unwrapped
    return print(unwrapped)
  }) as ComputedRef<string>

  const { orderType } = useOrderingType(qstr)

  const { dataType } = useRootType(qstr)

  const findDataField = (field: SelectionNode | ExecutableDefinitionNode) => {
    if (field.kind === Kind.INLINE_FRAGMENT) return null
    if (field.name?.value === 'data') return field
    if (field.kind === Kind.FRAGMENT_SPREAD || !field.selectionSet) return null
    for (const fieldElement of field.selectionSet.selections) {
      const res = findDataField(fieldElement)
      if (res !== null) return res
    }
    return null
  }

  const orderIndex = computed(() => {
    if (orderType.value === null || !orderType.value.enumValues) return null
    const options = {
      includeScore: true,
      keys: ['name', 'description'],
    }

    return new Fuse(orderType.value.enumValues, options)
  })

  const fuzzySortOptions = (type) => {
    if (orderIndex.value === null) return []
    const res = orderIndex.value.search(type.name)
    if (res.length === 0) return []
    const match = res[0].item
    return generateSortOptions(match)
  }

  const fieldToColumn = (field: FieldTypeFragment | undefined): GraphqlColumnMap | null => {
    if (!field) return null
    const type = getFieldTypeLeaf(useFragment(TypeRefFragmentDef, field.type))

    const key = `${field.name}:${type.name}:${type.kind}`
    if (ColumnMap[key]) {
      return ColumnMap[key]
    }

    if ((type.kind as unknown as TypeKind) !== TypeKind.SCALAR) {
      return null
    }

    const sortOptions = fuzzySortOptions(field)

    switch (type.name) {
      case 'Date':
        return {
          component: markRaw(DateColumn),
          columnDef: {
            title: fieldToReadAble(field.name),
            key: field.name,
            orderAble: sortOptions.length > 0,
            renderUsing: {
              component: markRaw(DateColumn),
              componentProps: (row: any) => ({
                date: row[field.name],
                format: 'YYYY-MM-DD',
                fallback: '--',
              }),
            },
          },
          dataDescriber: [
            {
              title: fieldToReadAble(field.name),
              extraction: (data) => data[field.name],
            },
          ],
          sort: sortOptions,
        }
      default:
        return {
          component: markRaw(TdText),
          columnDef: {
            title: fieldToReadAble(field.name),
            key: field.name,
            orderAble: sortOptions.length > 0,
            renderUsing: {
              component: markRaw(TdText),
              componentProps: (row: any) => ({
                text: row[field.name],
              }),
            },
          },
          dataDescriber: [
            {
              title: fieldToReadAble(field.name),
              extraction: (data) => data[field.name],
            },
          ],
          sort: sortOptions,
        }
    }
  }

  const findFragmentPath = (field: SelectionNode) => {
    switch (field.kind) {
      case Kind.FRAGMENT_SPREAD:
        return [field.name.value]
      case Kind.FIELD: {
        if (!field.selectionSet) return [field.name.value]
        const path = field.name.value
        return field.selectionSet.selections.flatMap((selectionField: SelectionNode) =>
          findFragmentPath(selectionField).map((p) => `${path}.${p}`),
        )
      }
    }
    return []
  }

  const columns = computed(() => {
    const definition = unref(query).definitions.find(({ kind }) => kind === OperationDefinition)
    if (!definition || !definition.name || dataType.value === null) {
      return []
    }

    const dataSelections = findDataField(definition)?.selectionSet?.selections ?? []
    return dataSelections
      .flatMap(
        (field: SelectionNode): { name: string; columnDescriber: GraphqlColumnMap | null }[] => {
          switch (field.kind) {
            case Kind.FRAGMENT_SPREAD:
              return [{ name: field.name.value, columnDescriber: ColumnMap[field.name.value] }]
            case Kind.FIELD:
              if (!field.selectionSet && dataType.value) {
                // We are in the likely case just being a field and not something more complicated
                const fieldType: FieldTypeFragment | undefined = dataType.value.fields
                  ?.map((f) => useFragment(FieldTypeFragmentDef, f))
                  .find((ft) => ft.name === field.name.value)
                return [{ name: field.name.value, columnDescriber: fieldToColumn(fieldType) }]
              }
              // We are likely more complicated so we will need to do more processing to figure out what
              // we are looking at.
              return findFragmentPath(field)
                .filter(Boolean)
                .map((path: string) => {
                  const parts = path.split('.')
                  const fragName = parts[parts.length - 1]
                  const column = ColumnMap[fragName]

                  // LIST mode: skip — expansion is handled by GraphqlDataTable.vue
                  if (column?.listSupport?.supportType === ColumnFragmentListSupport.LIST) {
                    return { name: fragName, columnDescriber: null }
                  }

                  // The fragment name is bad or something has gone wrong
                  if (!column) {
                    if (import.meta.env.DEV) {
                      // eslint-disable-next-line no-console
                      console.warn('Unknown fragment name', fragName)
                    }
                    return {
                      name: fragName,
                      columnDescriber: null,
                    }
                  }
                  const curryProps = parts.reduce(
                    (cur, p) => {
                      if (p === fragName) return cur
                      return (row: object) => cur(row[p])
                    },
                    column?.columnDef?.renderUsing?.componentProps ?? ((row: object) => row),
                  ) // We extract the props so they are usable to the column
                  const def: Column = deepClone(column.columnDef)
                  if (def.renderUsing) {
                    def.renderUsing.componentProps = curryProps

                    // We are handling the unwrapping of the should render logic
                    def.renderUsing.shouldRender = parts.reduce(
                      (cur, p) => {
                        if (p === fragName) return cur
                        return (row: object) => {
                          if (row[p]) {
                            return cur(row[p])
                          }
                          return false
                        }
                      },
                      // In case we don't have a handler function we always try to render
                      def.renderUsing.shouldRender ?? (() => true),
                    )
                  }
                  return {
                    name: fragName,
                    columnDescriber: {
                      ...column,
                      columnDef: def,
                      path: parts.slice(0, parts.length - 1).join('.'),
                    },
                  }
                })
          }
          // Inline fragment case, not implemented as of now. See more https://www.geeksforgeeks.org/graphql/inline-fragments-in-graphql/
          return [
            {
              name: field.kind,
              columnDescriber: null,
            },
          ]
        },
      )
      .filter(({ columnDescriber }) => columnDescriber !== null)
  })

  return { columns, fieldToColumn }
}

export const useQueryOperationDefinition = (query: MaybeRef<string | TypedQueryDocumentNode>) => {
  const q = computed(() => {
    const unwrapped = unref(query)
    if (typeof unwrapped === 'string') return gql(unwrapped)
    return unwrapped
  }) as ComputedRef<TypedQueryDocumentNode>

  const queryOperationDefinition = computed(
    () => q.value.definitions.find(({ kind }) => kind === OperationDefinition) ?? null,
  )

  return { queryOperationDefinition }
}

export type ExpandedColumnEntry = { name: string; columnDescriber: GraphqlColumnMap }

export const useExpandedColumns = (
  columnsWhiteList: Ref<Record<string, boolean>>,
  expandedColumnTitles: Ref<Record<string, string>>,
): ComputedRef<ExpandedColumnEntry[]> =>
  computed(() => {
    const entries: ExpandedColumnEntry[] = []
    const byBase = new Map<string, string[]>()

    Object.keys(columnsWhiteList.value).forEach((key) => {
      if (!columnsWhiteList.value[key]) return
      const expanded = decodeExpandedColumnKey(key)
      if (!expanded) return
      const base = ColumnMap[expanded.fragmentName]
      if (base?.listSupport?.supportType !== ColumnFragmentListSupport.LIST) return
      if (!byBase.has(expanded.fragmentName)) byBase.set(expanded.fragmentName, [])
      byBase.get(expanded.fragmentName)!.push(key)
    })

    for (const [baseName, keys] of byBase) {
      const base = ColumnMap[baseName]
      const config = base.listSupport as ListSupportExpand

      for (const expandedKey of keys) {
        const itemKey = decodeExpandedColumnKey(expandedKey)?.itemKey ?? ''
        const title = expandedColumnTitles.value[expandedKey] ?? base.columnDef.title
        const originalProps =
          base.columnDef.renderUsing?.componentProps ?? ((r: any) => ({ row: r }))

        const def: Column = {
          ...deepClone(base.columnDef),
          title,
          key: expandedKey,
          renderUsing: {
            component: base.columnDef.renderUsing!.component,
            componentProps: (row: any) => {
              const list = (dotAccess(row, config.listFieldName) as any[] | undefined) ?? []
              const item = list.find((el: any) => config.keyOf(el) === itemKey)
              return originalProps(item ?? {})
            },
          },
        }

        entries.push({
          name: expandedKey,
          columnDescriber: {
            ...base,
            columnDef: def,
            dataDescriber: base.dataDescriber.map((d) => ({
              title: `${title} - ${d.title}`,
              extraction: (row: any) => {
                const list = (dotAccess(row, config.listFieldName) as any[] | undefined) ?? []
                const item = list.find((el: any) => config.keyOf(el) === itemKey)
                return item ? d.extraction(item) : '-'
              },
            })),
            path: config.listFieldName,
          },
        })
      }
    }

    return entries
  })

export const useColumnOrdering: (query: MaybeRef<string>) => {
  columnsWhiteList: RemovableRef<{
    [p: string]: boolean
  }>
  expandedColumnTitles: RemovableRef<Record<string, string>>
  columnOrdering: RemovableRef<string[]>
  defaultColumnSelection: ComputedRef<{
    [p: string]: boolean
  }>
  defaultColumnOrdering: ComputedRef<string[]>
  resetColumnSelection: () => void
  resetOrdering: () => void
  dragEventHandler: (
    event: 'dragover' | 'dragstart' | 'dragend' | 'drag' | 'dragenter' | 'dragleave',
    col: Column,
    overwriteDraggable?: boolean,
  ) => void
} = (query: MaybeRef<string>) => {
  const { queryName } = useQueryType(query)

  const { columns: defaultColumns } = useQueryColumns(query)

  const defaultColumnSelection = computed(() =>
    defaultColumns.value.reduce(
      (acc: Record<string, boolean>, { name }) => ({ ...acc, [name]: true }),
      {},
    ),
  )

  const columnsWhiteList = useLocalStorage(
    `${queryName.value}-gql-data-table`,
    defaultColumnSelection.value,
    {
      mergeDefaults: true,
    },
  )

  const columnOrdering = useLocalStorage<string[]>(`${queryName.value}-columnOrdering`, [])
  const expandedColumnTitles = useLocalStorage<Record<string, string>>(
    `${queryName.value}-expandedTitles`,
    {},
  )
  const defaultColumnOrdering = computed(() =>
    defaultColumns.value
      .filter(({ columnDescriber }) => !!columnDescriber)
      .map(
        ({
          columnDescriber: {
            columnDef: { key },
          },
        }) => key,
      ),
  )

  const resetOrdering = () => {
    columnOrdering.value = [...defaultColumnOrdering.value]
  }
  const resetColumnSelection = () => {
    columnsWhiteList.value = { ...defaultColumnSelection.value }
    resetOrdering()
  }

  watch(
    defaultColumnSelection,
    () => {
      const defaults = defaultColumnSelection.value
      const saved = columnsWhiteList.value

      // It gets rebuild from the defaults, but also uses the saved values safely.
      columnsWhiteList.value = Object.fromEntries(
        Object.entries(defaults).map(([key, defaultVal]) => [
          key,
          Object.hasOwn(saved, key) ? saved[key] : defaultVal,
        ]),
      )

      if (columnOrdering.value.length === 0) {
        resetOrdering()
      }
    },
    { immediate: true },
  )

  const draggedColumn: Ref<null | Column> = ref(null)
  const lastSwapTargetKey = ref<null | string>(null)
  const dragEventHandler = (
    event: 'dragover' | 'dragstart' | 'dragend' | 'drag' | 'dragenter' | 'dragleave',
    col: Column,
    overwriteDraggable = false,
  ) => {
    if (columnOrdering.value.length === 0) {
      // Let's fill the ordering with the default list
      resetOrdering()
    }
    switch (event) {
      case 'dragstart':
        draggedColumn.value = col
        break
      case 'dragend':
        draggedColumn.value = null
        lastSwapTargetKey.value = null
        break
      case 'dragover':
        if (
          (col.draggable || overwriteDraggable) &&
          draggedColumn.value !== null &&
          col.key !== draggedColumn.value?.key &&
          col.key !== lastSwapTargetKey.value
        ) {
          const order: Array<string> = [...columnOrdering.value]

          const draggedKey = draggedColumn.value.key
          const targetKey = col.key

          const from = order.indexOf(draggedKey)
          const to = order.indexOf(targetKey)

          if (from === -1 || to === -1) return

          order.splice(from, 1)

          order.splice(to, 0, draggedKey)

          lastSwapTargetKey.value = col.key
          columnOrdering.value = order
        }
    }
  }

  return {
    columnsWhiteList,
    expandedColumnTitles,
    columnOrdering,
    defaultColumnSelection,
    defaultColumnOrdering,
    resetColumnSelection,
    resetOrdering,
    dragEventHandler,
  }
}

/** Which table owns the bare keys; ownership, not a count, since the first table may unmount before the second. */
let bareOwner: symbol | null = null

const namespacedKeys = (params: URLSearchParams, namespace: string) =>
  [...params.keys()].filter((key) => key.startsWith(`${namespace}-`))

function dropKeys(keys: string[]): void {
  const params = new URLSearchParams(window.location.search)
  const present = keys.filter((key) => params.has(key))

  if (present.length === 0) return

  present.forEach((key) => GlobalEncoder.push({ key, query: null }))

  GlobalEncoder.forceUpdateHistory()
}

/** Move state a link left under this namespace onto the bare keys the table will use. */
function adoptState(namespace: string): void {
  if (namespace === '') return

  const params = new URLSearchParams(window.location.search)
  const owned = namespacedKeys(params, namespace)

  if (owned.length === 0) return

  owned.forEach((key) => {
    const bare = key.slice(namespace.length + 1)

    if (!params.has(bare)) GlobalEncoder.push({ key: bare, query: params.get(key) ?? '' })

    GlobalEncoder.push({ key, query: null })
  })

  // Flushed, not debounced: encoders read the URL as they are constructed.
  GlobalEncoder.forceUpdateHistory()
}

/** Clear this namespace's keys, so a closed detail view leaves none behind. */
function dropState(namespace: string): void {
  if (namespace === '') return

  dropKeys(namespacedKeys(new URLSearchParams(window.location.search), namespace))
}

/** Table URL state, namespaced by operation name only when another table holds the bare keys; adopted on open, cleared on unmount. */
export const useTableUrlState = (query: MaybeRef<TypedQueryDocumentNode>) => {
  // An unnamed operation has no namespace to fall back on.
  const ownNamespace = tableNamespace(unref(query)) || 'graphqlTable'
  const token = Symbol('graphqlTable')
  const ownsBareKeys = bareOwner === null

  if (ownsBareKeys) bareOwner = token

  const namespace = ownsBareKeys ? '' : ownNamespace

  if (namespace === '') adoptState(ownNamespace)

  const bareKeys = new Set<string>()
  const mountedPath = window.location.pathname

  // Before unmount: closing an overlay navigates first, so dropping later would hit the list's query string.
  onBeforeUnmount(() => {
    if (bareOwner === token) bareOwner = null

    dropState(namespace)

    // Same path means a tab or segment switch, whose next table must not inherit this one's sort or filters.
    if (window.location.pathname === mountedPath) dropKeys([...bareKeys])
  })

  let parked: [string, string][] = []
  const forgetParked = () => {
    parked = []
  }

  // Hidden by KeepAlive: park the state out of the URL, so a link copied meanwhile doesn't carry it.
  onDeactivated(() => {
    const params = new URLSearchParams(window.location.search)
    const owned = namespace ? namespacedKeys(params, namespace) : [...bareKeys]

    parked = owned.filter((key) => params.has(key)).map((key) => [key, params.get(key) ?? ''])
    dropKeys(owned)

    // A history step re-decodes the encoders from that entry, so the parked state is stale.
    window.addEventListener('popstate', forgetParked, { once: true })
  })

  onActivated(() => {
    window.removeEventListener('popstate', forgetParked)

    if (parked.length === 0) return

    parked.forEach(([key, value]) => GlobalEncoder.push({ key, query: value }))
    parked = []

    GlobalEncoder.forceUpdateHistory()
  })

  onBeforeUnmount(() => window.removeEventListener('popstate', forgetParked))

  const persistedKey = (name: string) => {
    if (namespace) return `${namespace}-${name}`

    bareKeys.add(name)

    return name
  }

  return { namespace, persistedKey }
}
